import { useState } from 'react'
import { reverseGeocode } from '../lib/geocode'

export interface GeolocateResult {
  lat: number
  lng: number
  label: string | null
}

const UNSUPPORTED_MESSAGE = "Your browser doesn't support location search."
const FALLBACK_MESSAGE = "Couldn't get your location. Try again or enter it manually."
const ERROR_MESSAGES: Record<number, string> = {
  1: 'Location access was denied. Enable it in your browser settings to search near you.',
  2: "Couldn't determine your location. Try again or enter it manually.",
  3: 'Location request timed out. Try again or enter it manually.',
}

/**
 * On-demand "use my current location" trigger for the discover search boxes.
 * Unlike useCurrentPosition (which auto-fires once on mount to seed the initial
 * center), this returns a `locate` callback wired to a clickable pin: it reads
 * GPS, reverse-geocodes a human-readable label, and hands both back to the
 * caller. `locating` reflects the in-flight state so the pin can be disabled.
 * `error` surfaces why a request failed (denied/unavailable/timeout/unsupported)
 * instead of silently reverting the pin with no feedback.
 */
export function useGeolocate() {
  const [locating, setLocating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function locate(onLocated: (result: GeolocateResult) => void) {
    if (!navigator.geolocation) {
      setError(UNSUPPORTED_MESSAGE)
      return
    }
    setError(null)
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords
        try {
          const label = await reverseGeocode(latitude, longitude)
          onLocated({ lat: latitude, lng: longitude, label })
        } finally {
          setLocating(false)
        }
      },
      (err) => {
        setError(ERROR_MESSAGES[err.code] ?? FALLBACK_MESSAGE)
        setLocating(false)
      },
      { timeout: 8000 },
    )
  }

  return { locating, locate, error }
}
